/**
 * Utility function to get user country from ipinfo API or localStorage
 * 
 * This function will:
 * 1. Check if ipinfo_lang is already set in localStorage
 * 2. If set, return that value
 * 3. If not, make a call to ipinfo API and store the country in localStorage
 * 
 * @param {Function} callback - Function to call with the country code
 * @returns {Promise<string>} - Promise that resolves to the country code
 */
export const getUserCountry = async (callback) => {
  try {
    // Check if we already have the country stored in localStorage
    const storedCountry = localStorage.getItem('ipinfo_lang');
    
    if (storedCountry) {
      // If we have a stored country, use that
      if (callback && typeof callback === 'function') {
        callback(storedCountry);
      }
      return storedCountry;
    }
    
    // If we don't have a stored country, fetch it from ipinfo
    const response = await fetch('https://ipinfo.io/?token=284bb54968efbb');
    const data = await response.json();
    
    if (data.country) {
      // Store the country code in localStorage for future use
      localStorage.setItem('ipinfo_lang', data.country);
      
      // Call the callback if provided
      if (callback && typeof callback === 'function') {
        callback(data.country);
      }
      
      return data.country;
    }
    
    return null;
  } catch (err) {
    console.log('Error detecting user country:', err.message);
    return null;
  }
};

/**
 * Check if user is from a specific country
 * 
 * @param {string|string[]} countryCode - Country code or array of country codes to check against
 * @param {Function} callback - Function to call with the result (true/false)
 * @returns {Promise<boolean>} - Promise that resolves to true if user is from specified country
 */
export const isUserFromCountry = async (countryCode, callback) => {
  const userCountry = await getUserCountry();
  
  if (!userCountry) return false;
  
  const isMatch = Array.isArray(countryCode) 
    ? countryCode.includes(userCountry)
    : userCountry === countryCode;
  
  if (callback && typeof callback === 'function') {
    callback(isMatch);
  }
  
  return isMatch;
}; 